// Copyright ©️ 2026 eVolve MEP, LLC

import type { DesktopAuthorizationCode, PkceCodeChallenge } from '@evolvemep/api-ts';

import { isNil, isNotNil } from 'helpers/isNotNil';

import type { LoginErrorCode } from '../components/LoginErrorAlert';

export const desktopAuthPath = 'desktop-auth';

/**
 * The name the sign-in page shows for each desktop app, by OAuth 2.0 client ID. The Admin API owns which IDs are
 * valid (`DesktopClients` in `admin`), so an ID missing here is assumed to be a newly registered app and the page
 * falls back to a generic title.
 */
export const desktopClients = {
  'revit-plugin': 'EVOLVE Revit',
  'machine-client': 'EVOLVE Machine Client',
  '360-sync': 'EVOLVE 360 Sync',
} as const;

const errorCodeParam = 'errorCode';

/** Extracts the error code a failed SSO round trip adds from the query string the desktop app sent. */
export const extractDesktopAuthErrorCode = (searchParams: URLSearchParams) => {
  const desktopRequest = new URLSearchParams(searchParams);
  desktopRequest.delete(errorCodeParam);
  return { desktopRequest: desktopRequest.toString(), errorCode: searchParams.get(errorCodeParam) ?? undefined };
};

export const buildDesktopAuthUrl = (desktopRequest: string, errorCode: LoginErrorCode | string) => {
  const searchParams = new URLSearchParams(desktopRequest);
  searchParams.set(errorCodeParam, errorCode);
  return `/${desktopAuthPath}?${searchParams.toString()}`;
};

declare const desktopAuthStateSymbol: unique symbol;
/** Optional opaque value generated by the desktop app, echoed back to it unchanged. */
type DesktopAuthState = string & { [desktopAuthStateSymbol]: never };

export type DesktopAuthRequest = {
  clientId: string;
  redirectUri: string;
  state?: DesktopAuthState;
  codeChallenge: PkceCodeChallenge;
};

type ParsedDesktopAuthRequest =
  | ({ valid: true } & DesktopAuthRequest)
  | {
      valid: false;
      reason: string;
      /**
       * Hands the error back to the desktop app. Absent when `redirect_uri` is invalid or `client_id` is missing,
       * so there is nowhere safe to send it (RFC 6749 section 4.1.2.1).
       */
      errorCallbackUrl?: string;
    };

// S256 challenges are always 43 base64url characters (a 32-byte SHA-256, unpadded)
const s256ChallengePattern = /^[A-Za-z0-9_-]{43}$/;
const isS256Challenge = (value: string): value is PkceCodeChallenge => s256ChallengePattern.test(value);

const loopbackHostnames: readonly string[] = ['127.0.0.1', '[::1]'];

const tryParseUrl = (value: string) => {
  try {
    return new URL(value);
  } catch {
    return undefined;
  }
};

const isLoopbackRedirectUri = (value: string) => {
  const url = tryParseUrl(value);
  if (isNil(url)) return false;
  return (
    url.protocol === 'http:' &&
    loopbackHostnames.includes(url.hostname) &&
    url.port !== '' &&
    url.username === '' &&
    url.password === '' &&
    url.hash === ''
  );
};

type DesktopAuthRequestError = 'invalid_request' | 'unsupported_response_type';

type LoopbackCallbackParams = { state?: DesktopAuthState } & (
  | { code: DesktopAuthorizationCode }
  | { error: 'access_denied' }
  | { error: DesktopAuthRequestError; error_description: string }
);

/** Builds the URL that hands control back to the desktop app's loopback listener. */
export const buildLoopbackCallbackUrl = (redirectUri: string, params: LoopbackCallbackParams) => {
  const url = new URL(redirectUri);
  Object.entries(params).forEach(([key, value]) => {
    if (isNotNil(value)) url.searchParams.set(key, value);
  });
  return url.toString();
};

/**
 * Validates the query params a desktop app sends when it opens `/desktop-auth`
 * (OAuth 2.0 for Native Apps, RFC 8252, with PKCE).
 */
export const parseDesktopAuthParams = (searchParams: URLSearchParams): ParsedDesktopAuthRequest => {
  const redirectUri = searchParams.get('redirect_uri');
  const clientId = searchParams.get('client_id');
  const rawState = searchParams.get('state');
  const responseType = searchParams.get('response_type');
  const codeChallenge = searchParams.get('code_challenge');
  const codeChallengeMethod = searchParams.get('code_challenge_method');

  if (isNil(redirectUri) || !isLoopbackRedirectUri(redirectUri)) {
    return {
      valid: false,
      reason: `redirect_uri must be ${loopbackHostnames.map((hostname) => `http://${hostname}`).join(' or ')} with an explicit port.`,
    };
  }
  if (!clientId) {
    return { valid: false, reason: 'client_id is required.' };
  }

  const state = !rawState ? undefined : (rawState as DesktopAuthState);
  const reject = (error: DesktopAuthRequestError, reason: string): ParsedDesktopAuthRequest => ({
    valid: false,
    reason,
    errorCallbackUrl: buildLoopbackCallbackUrl(redirectUri, { error, error_description: reason, state }),
  });

  if (isNil(responseType)) {
    return reject('invalid_request', 'response_type is required.');
  }
  if (responseType !== 'code') {
    return reject('unsupported_response_type', 'response_type must be code.');
  }
  if (codeChallengeMethod !== 'S256') {
    return reject('invalid_request', 'code_challenge_method must be S256.');
  }
  if (isNil(codeChallenge) || !isS256Challenge(codeChallenge)) {
    return reject('invalid_request', 'code_challenge must be a 43-character base64url S256 challenge.');
  }

  return { valid: true, clientId, redirectUri, state, codeChallenge };
};
